import { getUserDetails } from 'util/api'
import { getCache, setCache, verifyLogin } from 'util/cache'
import { useDocTitle } from 'util/hooks'
import { useGetLessonsSearchQuery } from 'util/rtk-api'
import { classifySubject, SUBJECT_TAG_CLASS } from 'util/subject-tags'

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import 'css/pages/dashboard.scss'

const timeOfDayGreeting = (): string => {
	const hour = new Date().getHours()
	if (hour < 12) return 'Good morning'
	if (hour < 17) return 'Good afternoon'
	return 'Good evening'
}

// Every lesson always carries exactly 3 difficulty tiers (Emerging /
// Intermediate / Advanced) -- see ASSIGNED_DIFFICULTIES in shared-types --
// so "3 tiers" is a real, constant fact about every lesson, not a stat we
// have to compute.
const LESSON_TIER_COUNT = 3

const gradeLabel = (grades?: string[]): string => {
	const g = (grades?.[0] || '').trim().toUpperCase()
	if (!g) return ''
	if (g === 'K') return 'Grade K'
	if (g === 'T') return 'Ages 18+'
	return `Grade ${g}`
}

const relativeDay = (timestamp?: number): string => {
	if (!timestamp) return ''
	const days = Math.floor((Date.now() - timestamp) / (24 * 60 * 60 * 1000))
	if (days <= 0) return 'today'
	if (days === 1) return 'yesterday'
	if (days < 7) return new Date(timestamp).toLocaleDateString('en-US', { weekday: 'long' })
	if (days < 14) return 'last week'
	return `${Math.floor(days / 7)} weeks ago`
}

export const Dashboard = () => {
	const navigate = useNavigate()

	const [name, setName] = useState(getCache('firstName'))
	const [ready, setReady] = useState(false)
	const [loadFailed, setLoadFailed] = useState(false)

	const initializeUserDetails = useCallback(async () => {
		const verified = verifyLogin(navigate)

		if (!verified) return

		// err-8: a failed details fetch used to leave an infinite "Loading..."
		// plus an unhandled rejection
		let userDetails
		try {
			userDetails = await getUserDetails()
		} catch (e) {
			setLoadFailed(true)
			return
		}

		if (userDetails?.accountType === 'student') {
			navigate('/enter-code', { replace: true })
			return
		}

		setCache(userDetails)
		setName(userDetails.firstName)
		setLoadFailed(false)
		setReady(true)
	}, [navigate])

	useDocTitle('All for One - Dashboard')

	const { data: lessonsData } = useGetLessonsSearchQuery(undefined, { skip: !ready })
	const recentLessons = useMemo(
		() => [...(lessonsData?.lessons || [])]
			.sort((a, b) => (b.lastViewed || 0) - (a.lastViewed || 0))
			.slice(0, 3),
		[lessonsData]
	)

	useEffect(() => {
		verifyLogin(navigate)
		initializeUserDetails()
	}, [navigate, initializeUserDetails])

	if (loadFailed) {
		return (
			<div className="center-container">
				<h1 className="dashboard-header">We couldn't load your dashboard</h1>
				<p>Check your connection, then <a href="#retry" onClick={e => { e.preventDefault(); setLoadFailed(false); initializeUserDetails() }}>try again</a>.</p>
			</div>
		)
	}

	if (!ready) {
		return <div className="center-container"><h1 className="dashboard-header">Loading...</h1></div>
	}

	return (
		<div className="dashboard">
			<section className="dashboard-hero">
				{/* the mockup's .hero-inner -- caps the whole hero text block at
				    640px so the h1 breaks where the design breaks it. Without it
				    the h1 measured 1328px wide. */}
				<div className="dashboard-hero-inner">
					<span className="dashboard-eyebrow">{timeOfDayGreeting()}, {name}</span>
					<h1>Learning designed<br />for <em>every mind</em>.</h1>
					<div className="dashboard-hero-rule" />
					<p className="dashboard-hero-sub">Pick up a classroom, start a new lesson pathway, or jump back into something you've already built.</p>
				</div>
			</section>

			<div className="dashboard-action-grid">
				<div className="dashboard-panel dashboard-panel-primary">
					<div>
						<div className="dashboard-panel-icon">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#91B6D0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
						</div>
						<h2>Plan a new lesson</h2>
						<p>Upload or paste today's lesson and build differentiated pathways for every learner in the room.</p>
					</div>
					<button className="dashboard-cta-btn" onClick={() => navigate('/upload-lessons')}>
						Create new lesson
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
					</button>
				</div>
				<div className="dashboard-secondary-rail">
					<Link className="dashboard-panel dashboard-panel-secondary" to="/classrooms">
						<div className="dashboard-panel-icon">
							<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3A7BCD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
						</div>
						<div><h3>My classrooms</h3><p>Manage your students</p></div>
						<svg className="dashboard-panel-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
					</Link>
					<Link className="dashboard-panel dashboard-panel-secondary" to="/lessons">
						<div className="dashboard-panel-icon">
							<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3A7BCD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /></svg>
						</div>
						<div><h3>Lesson library</h3><p>Access your saved lessons</p></div>
						<svg className="dashboard-panel-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
					</Link>
				</div>
			</div>

			{recentLessons.length > 0 && (
				<div className="dashboard-library-section">
					<div className="dashboard-library-head">
						<h3>Recently in your library</h3>
						<Link to="/lessons">View all →</Link>
					</div>
					<div className="dashboard-lesson-row">
						{recentLessons.map(lesson => (
							<Link key={lesson.lessonId} className="dashboard-lesson-card" to={`/lesson-builder?lessonId=${lesson.lessonId}`}>
								<span className={`card-subj-tag ${SUBJECT_TAG_CLASS[classifySubject(lesson.subject)]}`}>
									<span className="card-subj-tag-dot" />
									{lesson.subject || 'Unassigned'} · {LESSON_TIER_COUNT} tiers
								</span>
								<h4>{lesson.name}</h4>
								<div className="dashboard-lesson-meta">
									{[gradeLabel(lesson.grades), `Last opened ${relativeDay(lesson.lastViewed)}`].filter(Boolean).join(' · ')}
								</div>
							</Link>
						))}
					</div>
				</div>
			)}
		</div>
	)
}